import { CreditCard, Money, Wallet, DeviceMobile, Bank, Handshake } from "@phosphor-icons/react";
import { filterPaymentMethods } from "@/shared/checkout/paymentMethodRules";

/** Icon + colour per payment method — keeps the selector visually distinctive. */
const META = {
  MB: { icon: Bank, label: "Multibanco", tint: "text-[#002480] bg-[#F5F5F2]" },
  MW: { icon: DeviceMobile, label: "MBWay", tint: "text-[#002480] bg-[#F5F5F2]", phone: true },
  TB: { icon: Money, label: "Transferência Bancária", tint: "text-[#0F1829] bg-[#F3F4F6]" },
  PP: { icon: Wallet, label: "PayPal", tint: "text-[#003087] bg-[#F3F4F6]" },
  CT: { icon: CreditCard, label: "Cartão de Crédito", tint: "text-[#0F1829] bg-[#F3F4F6]" },
  CP: { icon: Handshake, label: "Cofidis Pay (até 12x)", tint: "text-[#0F1829] bg-[#F3F4F6]" },
};

const INPUT = "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] transition-all";

const fmt = (v, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c || "EUR" }).format(v || 0);

export default function PaymentMethodSelector({
  methods = [], selected, onSelect,
  total = 0, currency = "EUR", billingNif = "",
  mbwayPhone, onMbwayPhoneChange,
  disabled = false,
}) {
  // Smart filter partilhado: esconde métodos com regras estritas (ex: NIF
  // empresa para Cofidis) e devolve regra de aviso quando aplicável.
  const visible = filterPaymentMethods(methods, { total, billingNif });

  const needsPhone = selected && META[selected]?.phone;

  return (
    <section className="bg-white rounded-xl border border-[#E5E7EB] p-5" data-testid="payment-method-section">
      <h2 className="text-base font-black tracking-tighter text-[#0F1829] mb-4" style={{ fontFamily: "'Fraunces', sans-serif" }}>
        Método de Pagamento
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {visible.map(({ method: m, rule }) => {
          const id = m.ID_TipoPagamento;
          const meta = META[id] || { icon: CreditCard, label: m.Descricao, tint: "text-[#0F1829] bg-[#F3F4F6]" };
          const Icon = meta.icon;
          const picked = selected === id;
          const blocked = !!rule;
          return (
            <button type="button" key={id}
              onClick={() => !blocked && !disabled && onSelect?.(id)}
              disabled={disabled || blocked}
              data-testid={`payment-method-${id}`}
              className={"text-left p-3 rounded-lg border flex items-center gap-3 transition-colors "
                + (picked ? "border-[#0F1829] bg-[#F5F5F2] ring-1 ring-[#0F1829]" : "border-[#E5E7EB] hover:bg-[#F9FAFB]")
                + (blocked || disabled ? " opacity-50 cursor-not-allowed" : "")}>
              <div className={"w-10 h-10 shrink-0 rounded-lg flex items-center justify-center " + meta.tint}>
                <Icon size={20} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0F1829] truncate">{meta.label || m.Descricao}</p>
                {blocked && (
                  <p className="text-[11px] text-[#FF2B2B] mt-0.5">{rule.reason}</p>
                )}
              </div>
              <div className={"w-4 h-4 rounded-full shrink-0 border-2 " + (picked ? "border-[#0F1829] bg-[#0F1829]" : "border-[#D1D5DB]")} />
            </button>
          );
        })}
      </div>

      {needsPhone && (
        <div className="mt-4 p-3 bg-[#F5F5F2] rounded-lg border border-[#E5E7EB]" data-testid="payment-mbway-phone">
          <label className="text-[11px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">
            Número de telemóvel (MBWay)
          </label>
          <input type="tel" className={INPUT} placeholder="351912345678"
            value={mbwayPhone} onChange={(e) => onMbwayPhoneChange?.(e.target.value.replace(/[^0-9+]/g, ""))}
            data-testid="payment-mbway-phone-input" />
          <p className="text-[11px] text-[#6B7280] mt-1">
            Introduza o número completo com indicativo (ex: 351912345678). Receberá uma notificação da App MBWay para autorizar o pagamento.
          </p>
        </div>
      )}
    </section>
  );
}
